import React, { useState } from 'react';
import { register } from '../api/api';

interface RegisterPageProps {
  onClose: () => void;
  onLoginClick: () => void;
}

const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 100 }, (_, i) => String(currentYear - 18 - i));

const SLIDES = [
  {
    label: 'Neukundenbonus!',
    sub: 'Wir verdoppeln deine erste Einzahlung',
    highlight: 'BIS ZU 100 €',
    bg: 'from-gray-900 via-gray-800 to-gray-700',
  },
  {
    label: 'Täglich neue Wetten!',
    sub: 'Top-Quoten auf alle Sportarten',
    highlight: 'JETZT WETTEN',
    bg: 'from-red-900 via-red-800 to-red-700',
  },
];

const RegisterPage: React.FC<RegisterPageProps> = ({ onClose, onLoginClick }) => {
  const [slide, setSlide] = useState(0);
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register({
        email,
        username,
        password,
        currency: 'EUR',
        country_code: 'DE',
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-100 z-[100] flex flex-col overflow-y-auto">
      {/* Promo Banner */}
      <div className={`relative w-full bg-gradient-to-r ${SLIDES[slide].bg} text-white flex-shrink-0`} style={{ minHeight: 180 }}>
        {/* Back button */}
        <button
          onClick={onClose}
          className="absolute top-4 left-4 z-10 text-white"
          aria-label="Geri"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Banner text */}
        <div className="flex flex-col justify-center px-6 pt-12 pb-10">
          <p className="text-base font-medium mb-1">{SLIDES[slide].label}</p>
          <p className="text-sm opacity-90 mb-2">{SLIDES[slide].sub}</p>
          <p className="text-3xl font-black tracking-wide">{SLIDES[slide].highlight}</p>
        </div>

        {/* Pagination dots */}
        <div className="absolute bottom-4 right-4 flex gap-2">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                slide === i ? 'bg-white text-gray-900' : 'bg-white/30 text-white'
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Form area */}
      <div className="flex-1 bg-gray-100 px-5 pt-7 pb-10">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Registrierung</h1>
        <p className="text-sm text-gray-500 mb-6 leading-snug">
          Die Registrierung ist nur aus Deutschland möglich. Felder mit * sind Pflicht.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* Geburtsdatum */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">
              * Geburts­datum
            </label>
            <div className="grid grid-cols-3 gap-2">
              {/* Tag */}
              <div className="relative">
                <select
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  className="w-full appearance-none bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-700 focus:outline-none focus:border-gray-400"
                  required
                >
                  <option value="">Tag</option>
                  {DAYS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Monat */}
              <div className="relative">
                <select
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="w-full appearance-none bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-700 focus:outline-none focus:border-gray-400"
                  required
                >
                  <option value="">Monat</option>
                  {MONTHS.map((m, i) => (
                    <option key={i} value={String(i + 1).padStart(2, '0')}>{m}</option>
                  ))}
                </select>
                <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Jahr */}
              <div className="relative">
                <select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full appearance-none bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-700 focus:outline-none focus:border-gray-400"
                  required
                >
                  <option value="">Jahr</option>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Vorname */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">* Vorname</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-700 focus:outline-none focus:border-gray-400"
              required
            />
          </div>

          {/* Nachname */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">* Nachname</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-700 focus:outline-none focus:border-gray-400"
              required
            />
          </div>

          {/* Benutzername */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">* Benutzername</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-700 focus:outline-none focus:border-gray-400"
              required
              autoComplete="username"
            />
          </div>

          {/* E-Mail */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">* E-Mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-700 focus:outline-none focus:border-gray-400"
              required
              autoComplete="email"
            />
          </div>

          {/* Passwort */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">* Passwort</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-700 focus:outline-none focus:border-gray-400 pr-12"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Handynummer */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">
              Handynummer <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden focus-within:border-gray-400">
              {/* Country selector */}
              <div className="flex items-center gap-1.5 px-3 border-r border-gray-200 flex-shrink-0">
                <span className="text-xl leading-none">🇩🇪</span>
                <svg className="h-3.5 w-3.5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
                <span className="text-sm text-gray-700">+49</span>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="flex-1 px-4 py-3.5 text-sm text-gray-700 bg-transparent focus:outline-none"
                placeholder=""
                autoComplete="tel"
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-red-600 text-sm text-center -mt-1">{error}</p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-900 text-white font-semibold py-4 rounded-xl text-base hover:bg-gray-700 active:bg-gray-800 transition mt-2 disabled:opacity-60"
          >
            {loading ? 'Bitte warten…' : 'Weiter'}
          </button>

          {/* Login link */}
          <p className="text-center text-sm text-gray-600">
            Bereits registriert?{' '}
            <button
              type="button"
              onClick={onLoginClick}
              className="text-blue-600 hover:underline font-medium"
            >
              Jetzt anmelden
            </button>
          </p>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
